import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react';

/**
 * The prototype's `table.t`: 12.5px rows with hairline dividers, a sticky pale header, numbers
 * right-aligned and tabular. Use `Th`/`Td` with `align="right"` for numeric columns.
 */
export function Table({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className="overflow-x-auto">
      <table className={`w-full border-collapse text-[12.5px] ${className}`}>{children}</table>
    </div>
  );
}

type Align = 'left' | 'right' | 'center';
const ALIGN: Record<Align, string> = { left: 'text-left', right: 'text-right', center: 'text-center' };

export function Th({ align = 'left', className = '', children, ...rest }: ThHTMLAttributes<HTMLTableCellElement> & { align?: Align }) {
  return (
    <th {...rest} className={`border-line text-muted sticky top-0 whitespace-nowrap border-b bg-[#FAFCFE] px-2 py-[9px] text-[11px] font-semibold leading-tight ${ALIGN[align]} ${className}`}>
      {children}
    </th>
  );
}

export function Td({ align = 'left', className = '', children, ...rest }: TdHTMLAttributes<HTMLTableCellElement> & { align?: Align }) {
  return (
    <td {...rest} className={`border-line border-b px-2 py-2 align-middle tabular-nums ${ALIGN[align]} ${className}`}>
      {children}
    </td>
  );
}

/** A body row with the prototype's hover tint; `selected` tints it cyan. */
export function Tr({ selected = false, className = '', children, ...rest }: HTMLAttributes<HTMLTableRowElement> & { selected?: boolean }) {
  return (
    <tr {...rest} className={`${selected ? '[&>td]:bg-[#F0FAFD]' : 'hover:[&>td]:bg-hover'} ${className}`}>
      {children}
    </tr>
  );
}
